import { defineStore } from 'pinia'
import type { Quote, QuoteItem, QuoteStatus, NoteStatus, Client } from '@/types'
import { useAuthStore } from './useAuthStore'
import {
  fetchQuotes,
  createQuoteApi,
  updateQuoteApi,
  submitQuoteApi,
  validateQuoteApi,
  rejectQuoteApi,
  deleteQuoteApi,
  sendToPricingApi,
  setNoteStatusApi
} from '@/services/quoteApi'
import { isNoteItem, isNoteQuote } from '@/utils/noteMode'

// ============================================
// Constants
// ============================================

const TVA_RATE = 0.20 // 20% TVA
const SAVE_DEBOUNCE_MS = 1500

// ============================================
// State Interface
// ============================================

interface QuoteState {
  quotes: Quote[]
  currentQuoteId: string | null
  isLoading: boolean
  error: string | null
  lastSavedAt: number | null
  pendingCount: number
  notesPendingCount: number
  _saveTimers: Record<string, ReturnType<typeof setTimeout>>
}

// ============================================
// Helper Functions
// ============================================

function generateId(): string {
  return `quote-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

function generateItemId(): string {
  return `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

function calculateTotals(items: QuoteItem[]): { totalHT: number; totalTTC: number } {
  const totalHT = items.reduce((sum, item) => sum + item.totalPrice, 0)
  const totalTTC = totalHT * (1 + TVA_RATE)
  return {
    totalHT: Math.round(totalHT * 100) / 100,
    totalTTC: Math.round(totalTTC * 100) / 100
  }
}

// ============================================
// Store Definition
// ============================================

export const useQuoteStore = defineStore('quotes', {
  // ----------------------------------------
  // State
  // ----------------------------------------
  state: (): QuoteState => ({
    quotes: [],
    currentQuoteId: null,
    isLoading: false,
    error: null,
    lastSavedAt: null,
    pendingCount: 0,
    notesPendingCount: 0,
    _saveTimers: {}
  }),

  // ----------------------------------------
  // Getters
  // ----------------------------------------
  getters: {
    getById: (state) => {
      return (id: string): Quote | undefined => {
        return state.quotes.find(q => q.id === id)
      }
    },

    currentQuote(): Quote | null {
      if (!this.currentQuoteId) return null
      return this.quotes.find(q => q.id === this.currentQuoteId) || null
    },

    quotesByStatus: (state) => {
      return (status: QuoteStatus): Quote[] => {
        return state.quotes.filter(q => q.status === status)
      }
    },

    quotesForCurrentUser(): Quote[] {
      const authStore = useAuthStore()
      const userId = authStore.user?.id
      if (!userId) return []
      return this.quotes.filter(q => q.createdBy === userId)
    },

    pendingValidation(): Quote[] {
      return this.quotes.filter(q => q.status === 'attente_validation')
    },

    sortedByDate(): Quote[] {
      return [...this.quotes].sort((a, b) => b.createdAt - a.createdAt)
    },

    userQuotesSortedByDate(): Quote[] {
      const authStore = useAuthStore()
      const userId = authStore.user?.id
      if (!userId) return []
      return [...this.quotes]
        .filter(q => q.createdBy === userId)
        .sort((a, b) => b.createdAt - a.createdAt)
    },

    countByStatus(): Record<QuoteStatus, number> {
      const counts: Record<QuoteStatus, number> = {
        brouillon: 0,
        attente_validation: 0,
        valide_local: 0,
        synchronise: 0,
        rejete: 0,
        signature_pending: 0,
        signe: 0,
        note_a_chiffrer: 0,
        note_chiffree: 0,
        note_abandonnee: 0
      }
      this.quotes.forEach(q => {
        counts[q.status]++
      })
      return counts
    },

    totalCount(): number {
      return this.quotes.length
    },

    syncFailedQuotes(): Quote[] {
      return this.quotes.filter(q => q.syncFailed === true)
    },

    syncFailedCount(): number {
      return this.quotes.filter(q => q.syncFailed === true).length
    },

    // Metrics
    quotesToday(): Quote[] {
      const now = new Date()
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
      return this.quotes.filter(q => q.createdAt >= startOfDay && !isNoteQuote(q))
    },

    quotesThisWeek(): Quote[] {
      const now = new Date()
      const dayOfWeek = now.getDay()
      const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
      const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysSinceMonday)
      return this.quotes.filter(q => q.createdAt >= monday.getTime() && !isNoteQuote(q))
    },

    quotesThisMonth(): Quote[] {
      const now = new Date()
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime()
      return this.quotes.filter(q => q.createdAt >= startOfMonth && !isNoteQuote(q))
    },

    recentSyncedQuotes(): Quote[] {
      return [...this.quotes]
        .filter(q => q.status === 'synchronise')
        .sort((a, b) => b.updatedAt - a.updatedAt)
        .slice(0, 10)
    },

    quotesTodayCount(): number { return this.quotesToday.length },
    quotesThisWeekCount(): number { return this.quotesThisWeek.length },
    quotesThisMonthCount(): number { return this.quotesThisMonth.length },

    // Cart Getters
    cartItems(): QuoteItem[] {
      if (!this.currentQuoteId) return []
      const quote = this.quotes.find(q => q.id === this.currentQuoteId)
      return quote?.items || []
    },

    cartTotal(): number {
      if (!this.currentQuoteId) return 0
      const quote = this.quotes.find(q => q.id === this.currentQuoteId)
      return quote?.totalHT || 0
    },

    cartTotalTTC(): number {
      if (!this.currentQuoteId) return 0
      const quote = this.quotes.find(q => q.id === this.currentQuoteId)
      return quote?.totalTTC || 0
    },

    cartItemCount(): number {
      if (!this.currentQuoteId) return 0
      const quote = this.quotes.find(q => q.id === this.currentQuoteId)
      return quote?.items.length || 0
    }
  },

  // ----------------------------------------
  // Actions
  // ----------------------------------------
  actions: {
    // ---- API sync helpers ----

    /**
     * Load all quotes from backend
     */
    async loadFromApi(): Promise<void> {
      this.isLoading = true
      try {
        const { quotes, pending, notesPending } = await fetchQuotes()
        this.quotes = quotes
        this.pendingCount = pending
        this.notesPendingCount = notesPending
        console.log('[QuoteStore] Loaded from API:', quotes.length, 'quotes, pending:', pending)
      } catch (err) {
        console.warn('[QuoteStore] Failed to load from API, keeping local state:', err)
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Load note dossiers of one status (admin notes board).
     * Fetched by status so old notes are not cut by the default list limit.
     */
    async loadNotes(status: NoteStatus): Promise<void> {
      try {
        const { quotes, notesPending } = await fetchQuotes(status)
        // Drop local copies that left this status elsewhere (e.g. priced by another admin)
        const freshIds = new Set(quotes.map(q => q.id))
        this.quotes = this.quotes.filter(q => q.status !== status || freshIds.has(q.id))
        for (const fresh of quotes) {
          const index = this.quotes.findIndex(q => q.id === fresh.id)
          if (index === -1) this.quotes.push(fresh)
          else this.quotes[index] = fresh
        }
        this.notesPendingCount = notesPending
      } catch (err) {
        console.warn('[QuoteStore] Failed to load notes:', err)
      }
    },

    /**
     * Debounced save to backend — groups rapid edits
     */
    _scheduleSave(id: string): void {
      if (this._saveTimers[id]) {
        clearTimeout(this._saveTimers[id])
      }
      this._saveTimers[id] = setTimeout(() => {
        this._saveToApi(id)
        delete this._saveTimers[id]
      }, SAVE_DEBOUNCE_MS)
    },

    async _saveToApi(id: string): Promise<void> {
      const quote = this.quotes.find(q => q.id === id)
      if (!quote) return

      try {
        await updateQuoteApi(id, {
          client_id: quote.clientId || '',
          client_name: quote.client?.name || '',
          client_fresh_id: quote.client?.freshId || '',
          contact_id: quote.contactId || '',
          contact_name: quote.contactName || '',
          items: quote.items,
          total_ht: quote.totalHT,
          total_ttc: quote.totalTTC,
          notes: quote.notes || '',
          urgency: quote.urgency || 'standard',
          delivery_date: quote.deliveryDate || '',
          show_delivery: quote.showDelivery || false
        })
        this.lastSavedAt = Date.now()
        window.dispatchEvent(new CustomEvent('quote:saved'))
      } catch (err) {
        console.warn('[QuoteStore] Save to API failed:', err)
      }
    },

    // ---- CRUD actions ----

    async createQuote(clientId?: string): Promise<Quote> {
      const authStore = useAuthStore()
      const now = Date.now()

      const newQuote: Quote = {
        id: generateId(),
        clientId: clientId || '',
        status: 'brouillon',
        items: [],
        totalHT: 0,
        totalTTC: 0,
        createdBy: authStore.user?.id || '',
        createdAt: now,
        updatedAt: now
      }

      this.quotes.push(newQuote)
      this.currentQuoteId = newQuote.id

      // Persist to backend
      try {
        await createQuoteApi(newQuote)
        window.dispatchEvent(new CustomEvent('quote:saved'))
      } catch (err) {
        console.warn('[QuoteStore] Failed to create on API:', err)
      }

      console.log('[QuoteStore] Created quote:', newQuote.id)
      return newQuote
    },

    updateQuote(id: string, updates: Partial<Omit<Quote, 'id' | 'createdAt' | 'createdBy'>>): boolean {
      const index = this.quotes.findIndex(q => q.id === id)
      if (index === -1) {
        console.error('[QuoteStore] Quote not found:', id)
        return false
      }

      const quote = this.quotes[index]

      if (quote.status === 'synchronise' || quote.status === 'signe') {
        console.warn('[QuoteStore] Cannot edit synchronized/signed quote:', id)
        this.error = 'Ce devis ne peut plus être modifié'
        return false
      }

      this.quotes[index] = {
        ...quote,
        ...updates,
        updatedAt: Date.now()
      }

      if (updates.items) {
        const { totalHT, totalTTC } = calculateTotals(updates.items)
        this.quotes[index].totalHT = totalHT
        this.quotes[index].totalTTC = totalTTC
      }

      this._scheduleSave(id)
      console.log('[QuoteStore] Updated quote:', id)
      return true
    },

    async deleteQuote(id: string): Promise<boolean> {
      const index = this.quotes.findIndex(q => q.id === id)
      if (index === -1) {
        console.error('[QuoteStore] Quote not found:', id)
        return false
      }

      const quote = this.quotes[index]

      if (quote.status === 'synchronise' || quote.status === 'signe') {
        console.warn('[QuoteStore] Cannot delete synchronized/signed quote:', id)
        this.error = 'Ce devis ne peut pas être supprimé'
        return false
      }

      this.quotes.splice(index, 1)
      if (this.currentQuoteId === id) {
        this.currentQuoteId = null
      }

      try {
        await deleteQuoteApi(id)
      } catch (err) {
        console.warn('[QuoteStore] Failed to delete on API:', err)
      }

      console.log('[QuoteStore] Deleted quote:', id)
      return true
    },

    addItem(quoteId: string, item: Omit<QuoteItem, 'id'>): QuoteItem | null {
      const quote = this.quotes.find(q => q.id === quoteId)
      if (!quote) {
        console.error('[QuoteStore] Quote not found:', quoteId)
        return null
      }

      // Notes and products are exclusive within a dossier
      if (quote.items.length > 0 && isNoteQuote(quote) !== isNoteItem(item)) {
        this.error = 'Notes et produits ne peuvent pas être mélangés'
        console.error('[QuoteStore] Cannot mix notes and products:', quoteId)
        return null
      }

      const newItem: QuoteItem = { ...item, id: generateItemId() }
      quote.items.push(newItem)

      const { totalHT, totalTTC } = calculateTotals(quote.items)
      quote.totalHT = totalHT
      quote.totalTTC = totalTTC
      quote.updatedAt = Date.now()

      this._scheduleSave(quoteId)
      console.log('[QuoteStore] Added item to quote:', quoteId, newItem.id)
      return newItem
    },

    updateItem(quoteId: string, itemId: string, updates: Partial<Omit<QuoteItem, 'id'>>): boolean {
      const quote = this.quotes.find(q => q.id === quoteId)
      if (!quote) {
        console.error('[QuoteStore] Quote not found:', quoteId)
        return false
      }

      const itemIndex = quote.items.findIndex(i => i.id === itemId)
      if (itemIndex === -1) {
        console.error('[QuoteStore] Item not found:', itemId)
        return false
      }

      quote.items[itemIndex] = { ...quote.items[itemIndex], ...updates }

      const { totalHT, totalTTC } = calculateTotals(quote.items)
      quote.totalHT = totalHT
      quote.totalTTC = totalTTC
      quote.updatedAt = Date.now()

      this._scheduleSave(quoteId)
      console.log('[QuoteStore] Updated item:', itemId)
      return true
    },

    removeItem(quoteId: string, itemId: string): boolean {
      const quote = this.quotes.find(q => q.id === quoteId)
      if (!quote) {
        console.error('[QuoteStore] Quote not found:', quoteId)
        return false
      }

      const itemIndex = quote.items.findIndex(i => i.id === itemId)
      if (itemIndex === -1) {
        console.error('[QuoteStore] Item not found:', itemId)
        return false
      }

      quote.items.splice(itemIndex, 1)

      const { totalHT, totalTTC } = calculateTotals(quote.items)
      quote.totalHT = totalHT
      quote.totalTTC = totalTTC
      quote.updatedAt = Date.now()

      this._scheduleSave(quoteId)
      console.log('[QuoteStore] Removed item:', itemId)
      return true
    },

    clearCart(quoteId: string): boolean {
      const quote = this.quotes.find(q => q.id === quoteId)
      if (!quote) {
        console.error('[QuoteStore] Quote not found:', quoteId)
        return false
      }

      if (quote.status === 'synchronise' || quote.status === 'signe') {
        console.warn('[QuoteStore] Cannot clear synchronized/signed quote:', quoteId)
        this.error = 'Ce devis ne peut plus être modifié'
        return false
      }

      quote.items = []
      quote.totalHT = 0
      quote.totalTTC = 0
      quote.updatedAt = Date.now()

      this._scheduleSave(quoteId)
      console.log('[QuoteStore] Cart cleared for quote:', quoteId)
      return true
    },

    setCurrentQuote(id: string | null): void {
      this.currentQuoteId = id
      console.log('[QuoteStore] Current quote set to:', id)
    },

    // ---- Workflow actions (immediate API calls) ----

    async submitForValidation(id: string): Promise<boolean> {
      const quote = this.quotes.find(q => q.id === id)
      if (!quote) {
        console.error('[QuoteStore] Quote not found:', id)
        return false
      }

      if (quote.status !== 'brouillon' && quote.status !== 'rejete') {
        this.error = 'Seuls les brouillons peuvent être soumis'
        return false
      }

      if (!quote.clientId) {
        this.error = 'Un client doit être sélectionné'
        return false
      }

      if (quote.items.length === 0) {
        this.error = 'Le devis doit contenir au moins un produit'
        return false
      }

      // Flush any pending save first
      if (this._saveTimers[id]) {
        clearTimeout(this._saveTimers[id])
        delete this._saveTimers[id]
        await this._saveToApi(id)
      }

      try {
        await submitQuoteApi(id)
        quote.status = 'attente_validation'
        quote.updatedAt = Date.now()
        this.pendingCount = this.quotes.filter(q => q.status === 'attente_validation').length
        console.log('[QuoteStore] Quote submitted for validation:', id)
        return true
      } catch (err) {
        console.error('[QuoteStore] Submit failed:', err)
        this.error = 'Erreur lors de la soumission'
        return false
      }
    },

    async validateQuote(id: string): Promise<boolean> {
      const quote = this.quotes.find(q => q.id === id)
      if (!quote) {
        console.error('[QuoteStore] Quote not found:', id)
        return false
      }

      if (quote.status !== 'attente_validation') {
        this.error = 'Ce devis ne peut pas être validé'
        return false
      }

      try {
        await validateQuoteApi(id)
        quote.status = 'valide_local'
        quote.updatedAt = Date.now()
        this.pendingCount = this.quotes.filter(q => q.status === 'attente_validation').length
        console.log('[QuoteStore] Quote validated:', id)
        return true
      } catch (err) {
        console.error('[QuoteStore] Validate failed:', err)
        this.error = 'Erreur lors de la validation'
        return false
      }
    },

    async rejectQuote(id: string, reason?: string): Promise<boolean> {
      const quote = this.quotes.find(q => q.id === id)
      if (!quote) {
        console.error('[QuoteStore] Quote not found:', id)
        return false
      }

      if (quote.status !== 'attente_validation') {
        this.error = 'Ce devis ne peut pas être rejeté'
        return false
      }

      try {
        await rejectQuoteApi(id, reason || '')
        quote.status = 'rejete'
        if (reason) {
          quote.notes = (quote.notes || '') + `\n[Rejeté] ${reason}`
        }
        quote.updatedAt = Date.now()
        this.pendingCount = this.quotes.filter(q => q.status === 'attente_validation').length
        console.log('[QuoteStore] Quote rejected:', id)
        return true
      } catch (err) {
        console.error('[QuoteStore] Reject failed:', err)
        this.error = 'Erreur lors du rejet'
        return false
      }
    },

    /**
     * Note mode: send the dossier to the admin for pricing in Dolibarr
     */
    async sendToPricing(id: string): Promise<boolean> {
      const quote = this.quotes.find(q => q.id === id)
      if (!quote) {
        console.error('[QuoteStore] Quote not found:', id)
        return false
      }

      if (quote.status !== 'brouillon') {
        this.error = 'Seuls les brouillons peuvent être envoyés'
        return false
      }

      if (!quote.clientId) {
        this.error = 'Un client doit être sélectionné'
        return false
      }

      if (!isNoteQuote(quote)) {
        this.error = 'La note doit contenir au moins une note'
        return false
      }

      // Flush any pending save first
      if (this._saveTimers[id]) {
        clearTimeout(this._saveTimers[id])
        delete this._saveTimers[id]
        await this._saveToApi(id)
      }

      try {
        await sendToPricingApi(id)
        quote.status = 'note_a_chiffrer'
        quote.updatedAt = Date.now()
        this.notesPendingCount = this.quotes.filter(q => q.status === 'note_a_chiffrer').length
        console.log('[QuoteStore] Note sent to pricing:', id)
        return true
      } catch (err) {
        console.error('[QuoteStore] Send to pricing failed:', err)
        this.error = 'Erreur lors de l\'envoi'
        return false
      }
    },

    /**
     * Note mode (admin): priced / abandoned / reopened
     */
    async setNoteStatus(id: string, status: NoteStatus, noteRef?: string): Promise<boolean> {
      const quote = this.quotes.find(q => q.id === id)
      if (!quote) {
        console.error('[QuoteStore] Quote not found:', id)
        return false
      }

      try {
        const updated = await setNoteStatusApi(id, status, noteRef)
        quote.status = updated.status
        quote.noteRef = updated.noteRef
        quote.updatedAt = updated.updatedAt
        this.notesPendingCount = this.quotes.filter(q => q.status === 'note_a_chiffrer').length
        console.log('[QuoteStore] Note status set:', id, status)
        return true
      } catch (err) {
        console.error('[QuoteStore] Set note status failed:', err)
        this.error = 'Erreur lors du changement de statut'
        return false
      }
    },

    // ---- Other actions ----

    updateDelivery(id: string, urgency: 'standard' | 'urgent' | 'express', deliveryDate?: string): boolean {
      const quote = this.quotes.find(q => q.id === id)
      if (!quote) return false
      quote.urgency = urgency
      quote.deliveryDate = deliveryDate
      quote.updatedAt = Date.now()
      this._scheduleSave(id)
      return true
    },

    updateNotes(id: string, notes: string): boolean {
      const quote = this.quotes.find(q => q.id === id)
      if (!quote) {
        console.error('[QuoteStore] Quote not found:', id)
        return false
      }
      quote.notes = notes
      quote.updatedAt = Date.now()
      this._scheduleSave(id)
      console.log('[QuoteStore] Notes updated:', id)
      return true
    },

    updateInternalNotes(id: string, notes: string): boolean {
      const quote = this.quotes.find(q => q.id === id)
      if (!quote) {
        console.error('[QuoteStore] Quote not found:', id)
        return false
      }
      quote.internalNotes = notes
      quote.updatedAt = Date.now()
      console.log('[QuoteStore] Internal notes updated:', id)
      return true
    },

    setClient(id: string, clientId: string, client?: Client): boolean {
      const quote = this.quotes.find(q => q.id === id)
      if (!quote) {
        console.error('[QuoteStore] Quote not found:', id)
        return false
      }

      if (quote.status === 'synchronise' || quote.status === 'signe') {
        console.warn('[QuoteStore] Cannot edit synchronized/signed quote:', id)
        this.error = 'Ce devis ne peut plus être modifié'
        return false
      }

      quote.clientId = clientId
      quote.client = client
      quote.updatedAt = Date.now()

      this._scheduleSave(id)
      console.log('[QuoteStore] Client set for quote:', id, clientId)
      return true
    },

    clearError(): void {
      this.error = null
    },

    markAsSaved(): void {
      this.lastSavedAt = Date.now()
    },

    markSyncFailed(id: string, errorMessage?: string): boolean {
      const quote = this.quotes.find(q => q.id === id)
      if (!quote) {
        console.error('[QuoteStore] Quote not found:', id)
        return false
      }
      quote.syncFailed = true
      quote.syncFailedAt = Date.now()
      quote.syncErrorMessage = errorMessage
      quote.updatedAt = Date.now()
      console.log('[QuoteStore] Quote marked as sync failed:', id)
      return true
    },

    clearSyncFailed(id: string): boolean {
      const quote = this.quotes.find(q => q.id === id)
      if (!quote) {
        console.error('[QuoteStore] Quote not found:', id)
        return false
      }
      quote.syncFailed = false
      quote.syncFailedAt = undefined
      quote.syncErrorMessage = undefined
      quote.updatedAt = Date.now()
      console.log('[QuoteStore] Sync failed flag cleared:', id)
      return true
    },

    /**
     * Migrate localStorage quotes to backend (called once after login)
     */
    async migrateLocalStorage(): Promise<number> {
      const raw = localStorage.getItem('vfr-quotes')
      if (!raw) return 0

      try {
        const data = JSON.parse(raw)
        const localQuotes: Quote[] = data.quotes || []
        if (localQuotes.length === 0) return 0

        let migrated = 0
        for (const q of localQuotes) {
          try {
            await createQuoteApi(q)
            migrated++
          } catch {
            // Already exists or error — skip
          }
        }

        // Clear localStorage after successful migration
        localStorage.removeItem('vfr-quotes')
        console.log(`[QuoteStore] Migrated ${migrated}/${localQuotes.length} quotes from localStorage`)
        return migrated
      } catch {
        console.warn('[QuoteStore] Failed to parse localStorage quotes')
        return 0
      }
    }
  }

  // No more persist plugin — data comes from API
})

export default useQuoteStore
